import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trophy, Crown } from "lucide-react";

/** Compact strip that shows the player's personal best + top-5 global for an arcade game.
 *  Pass `refreshKey` (number) — bump it whenever a score is submitted to force a reload. */
export default function ArcadeScoreBar({ gameSlug, refreshKey = 0 }) {
  const { user } = useAuth();
  const [top, setTop] = useState([]);
  const [best, setBest] = useState(0);

  useEffect(() => {
    api.get(`/arcade/leaderboard/${gameSlug}?limit=5`)
      .then(r => setTop(r.data.entries || []))
      .catch(() => setTop([]));
    if (user) {
      api.get("/arcade/my-best")
        .then(r => setBest(r.data?.[gameSlug]?.score || 0))
        .catch(() => setBest(0));
    }
  }, [gameSlug, refreshKey, user?.username]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4" data-testid={`arcade-scores-${gameSlug}`}>
      <div className="border border-white/10 rounded-sm p-3" style={{ background: "rgba(0,0,0,0.35)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-3.5 h-3.5 text-[color:var(--sc-volt)]" />
          <div className="sc-mono text-[10px] uppercase tracking-widest text-zinc-500">Your best</div>
        </div>
        <div className="sc-heading text-2xl font-black text-[color:var(--sc-volt)]" data-testid={`best-${gameSlug}`}>
          {user ? best.toLocaleString() : "—"}
        </div>
        {!user && <div className="text-[10px] text-zinc-500 mt-1">Sign in to save your high score.</div>}
      </div>
      <div className="border border-white/10 rounded-sm p-3" style={{ background: "rgba(0,0,0,0.35)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-3.5 h-3.5 text-[#FFD700]" />
          <div className="sc-mono text-[10px] uppercase tracking-widest text-zinc-500">Global top 5</div>
        </div>
        {top.length === 0 && <div className="text-[11px] text-zinc-500">No scores yet — set the pace.</div>}
        {top.map((r, i) => (
          <div key={r.username + i} className="flex items-center justify-between text-[11px] py-0.5" data-testid={`top-${gameSlug}-${i}`}>
            <span className={i === 0 ? "text-[#FFD700] font-bold" : "text-zinc-300"}>
              {i + 1}. {r.username}
            </span>
            <span className="sc-mono font-bold text-[color:var(--sc-volt)]">{r.score.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
